"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import PublicLayout from "@/components/layout/PublicLayout";
import Link from "next/link";
import { Eye, EyeOff, Lock, Mail, ArrowRight, ShieldCheck, Sparkles, CheckCircle2 } from "lucide-react";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function LoginPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    import("@/lib/referral-tracking").then(({ initializeReferralTracking }) => {
      initializeReferralTracking();
    });

    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
      const existing = document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]');
      if (existing) {
        setRecaptchaReady(true);
        return;
      }
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.grecaptcha.ready(() => {
          setRecaptchaReady(true);
        });
      };
      document.head.appendChild(script);
    } else {
      setRecaptchaReady(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.email.trim() || !form.password.trim()) {
      setError("Please enter email and password");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      let recaptchaToken = "";
      if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
        if (!window.grecaptcha) {
          setError("Security verification not ready. Please try again.");
          setLoading(false);
          return;
        }
        recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "login" });
      }

      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      localStorage.removeItem("garage-data");
      localStorage.removeItem("user");

      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          recaptchaToken,
        }),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          setError(data.error || "Login failed");
        } catch {
          setError(`Login failed with status ${response.status}`);
        }
        return;
      }

      const data = await response.json();

      console.log("[LOGIN] Response data:", {
        role: data.user.role,
        hasReferral: data.user.hasReferral,
        hasGarage: data.user.hasGarage,
        email: data.user.email,
      });

      // Store user info
      localStorage.setItem("user", JSON.stringify(data.user));
      
      // Store garage data - handle both staff and owner roles
      if (data.garage) {
        localStorage.setItem("garage-data", JSON.stringify(data.garage));
      } else if (data.user?.staff?.garage) {
        localStorage.setItem("garage-data", JSON.stringify(data.user.staff.garage));
      } else if (data.user?.garages && data.user.garages[0]) {
        const mainGarage = data.user.garages.find((garage: any) => garage?.isMainLocation) || data.user.garages[0];
        localStorage.setItem("garage-data", JSON.stringify(mainGarage));
      }
      
      // Intelligent redirect based on entry point and user roles
      if (data.user.role === "ADMIN" || data.user.role === "SUPER_ADMIN") {
        console.log("[LOGIN] Redirecting to /admin (admin role)");
        router.push("/admin");
      } else if (data.user.hasReferral) {
        console.log("[LOGIN] Redirecting to /referrals (has referral program)");
        router.push("/referrals");
      } else if (data.user.hasGarage) {
        if (data.user.role === "GARAGE_STAFF") {
          console.log("[LOGIN] Redirecting to /garage/jobs (staff)");
          router.push("/garage/jobs");
        } else {
          console.log("[LOGIN] Redirecting to /garage/dashboard (has garage)");
          router.push("/garage/dashboard");
        }
      } else {
        console.log("[LOGIN] Redirecting to / (fallback)");
        router.push("/");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="relative min-h-[85vh] bg-linear-to-br from-slate-950 via-slate-900 to-blue-950 flex items-center justify-center px-4 sm:px-6 py-12 md:py-20 overflow-hidden font-sans">
        
        {/* Ambient Glows */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

        <div className="relative w-full max-w-md space-y-6">
          
          {/* Header Title */}
          <div className="text-center space-y-2">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
              GARAGE PORTAL ACCESS
            </span>
            <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              Welcome Back
            </h1>
            <p className="text-xs sm:text-sm text-slate-300">
              Sign in to manage your workshop, bookings & MOT reminders
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl p-7 sm:p-9 border-2 border-slate-200/90 shadow-2xl">
            {error && (
              <div className="mb-5 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-medium">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={17} />
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="your@email.com"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Password
                  </label>
                  <Link href="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700 font-semibold">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={17} />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full pl-10 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 px-3.5 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !recaptchaReady}
                className="w-full bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer mt-2"
              >
                <span>{loading ? "Signing in..." : "Sign In to Dashboard"}</span>
                <ArrowRight size={17} />
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-slate-100 text-center space-y-2">
              <p className="text-xs text-slate-600">
                Don&apos;t have a garage account yet?{" "}
                <Link href="/signup" className="text-blue-600 font-bold hover:underline">
                  Start 14-Day Free Trial
                </Link>
              </p>
            </div>
          </div>

          {/* Security note */}
          <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
            <ShieldCheck size={14} className="text-teal-400" />
            <span>256-bit SSL encrypted • UK DVSA API Connected</span>
          </div>

        </div>
      </div>
    </PublicLayout>
  );
}
